// Pure helpers for "where Boo checks in" (the deployment's default channel):
// the picker, the Channels tab card and the onboarding step. No React and no
// api client at runtime, so `node --test` covers it.

import type { ChannelPlatform, ChannelPlatformStatus, DefaultChannel } from "./api.ts";
import { shouldRetryQuery } from "./query-retry.ts";

/**
 * What the picker selects: an existing channel, no default channel, or a
 * channel staged for creation. A "new" channel is only created on Save /
 * Continue, so Cancel never leaves a stray channel behind.
 */
export type DefaultChannelChoice =
  | { kind: "none" }
  | {
      kind: "channel";
      platform: ChannelPlatform;
      channelOrSpaceId: string;
      displayName: string | null;
      surfaceType: string;
    }
  | { kind: "new"; platform: ChannelPlatform; name: string; isPrivate: boolean };

export function choiceFromCurrent(current: DefaultChannel | null | undefined): DefaultChannelChoice {
  return current
    ? {
        kind: "channel",
        platform: current.platform,
        channelOrSpaceId: current.channelOrSpaceId,
        displayName: current.displayName,
        surfaceType: current.surfaceType,
      }
    : { kind: "none" };
}

export const choiceKey = (choice: DefaultChannelChoice): string =>
  choice.kind === "none"
    ? "none"
    : choice.kind === "new"
      ? `new:${choice.platform}:${choice.name}`
      : `${choice.platform}:${choice.channelOrSpaceId}`;

export function sameChoice(a: DefaultChannelChoice, b: DefaultChannelChoice): boolean {
  return choiceKey(a) === choiceKey(b);
}

/** Platforms whose channels can be the default channel. */
export function defaultChannelPlatforms(
  statuses: ChannelPlatformStatus[] | undefined,
): ChannelPlatformStatus[] {
  return (statuses ?? []).filter(
    (p) => p.enabled && p.platform !== "dashboard_chat" && p.postsToChannels !== false,
  );
}

/**
 * Slack names arrive both bare (the channel list) and "#"-prefixed (records
 * stored before the brain normalized them); show one form.
 */
export function bareChannelName(name: string | null): string | null {
  return name === null ? null : name.replace(/^#+/, "") || null;
}

/** "#boo-updates", or the raw id when the platform gave no name. */
export function channelLabel(channel: {
  displayName: string | null;
  channelOrSpaceId: string;
  platform?: ChannelPlatform;
}): string {
  const name = bareChannelName(channel.displayName);
  if (!name) return channel.channelOrSpaceId;
  return channel.platform === undefined || channel.platform === "slack" || channel.platform === "discord"
    ? `#${name}`
    : name;
}

/** Slack's rules: lowercase, no spaces, letters/numbers/hyphens/underscores, 80 max. */
export function slackChannelName(raw: string): string {
  return raw
    .trim()
    .replace(/^#+/, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

/** Attempts while a Boo restarts (e.g. right after a chat platform connects): ~2 min at 3s apart. */
export const RESTART_RETRY_ATTEMPTS = 40;

/**
 * React Query `retry` for Boo-backed queries: the app-wide network-blip retry,
 * plus a 503 (the Boo restarting) until RESTART_RETRY_ATTEMPTS. Matched on
 * `status` structurally, like query-retry, to stay free of the api import.
 */
export function retryWhileRestarting(failureCount: number, error: unknown): boolean {
  if (shouldRetryQuery(failureCount, error)) return true;
  const status =
    typeof error === "object" && error !== null && "status" in error
      ? (error as { status?: unknown }).status
      : undefined;
  return status === 503 && failureCount < RESTART_RETRY_ATTEMPTS;
}
